import type { Milliperecent, SixtyThousandthsDegree } from './units.js';

/**
 * The animation and timing IR.
 *
 * PowerPoint's `p:timing` tree is SMIL: nested time containers whose leaves are
 * *behaviours* — "set style.visibility to visible", "animate ppt_x from #ppt_x
 * to 0.5", "apply the wipe(up) transition filter". Those behaviours are what the
 * file actually states, so they are what this IR keeps.
 *
 * `presetID` is deliberately carried as a raw number rather than translated into
 * a name. The published ECMA-376 schema types the attribute but does not
 * enumerate what each id means, and inventing that table would make the parser
 * assert things the specification does not say. Every visual decision the
 * compiler makes is therefore derived from the behaviours; the preset id travels
 * with the effect for diagnostics and round-tripping only.
 */

/** `p:cTn@presetClass`. `verb` and `mediacall` control media, not appearance. */
export type EffectClass = 'entrance' | 'emphasis' | 'exit' | 'motionPath' | 'mediaCall' | 'verb' | 'other';

/** How an effect starts relative to the rest of its click group. */
export type EffectTrigger = 'onClick' | 'withPrevious' | 'afterPrevious';

/** What a behaviour does when its active duration ends (`p:cTn@fill`). */
export type TimingFill = 'remove' | 'freeze' | 'hold' | 'transition';

export interface AnimationTarget {
  /** `p:spTgt@spid` — the same string as `ShapeBase.id`. */
  readonly shapeId: string;
  /** Set when `p:txEl/p:pRg` narrows the target to a paragraph range, 0-based inclusive. */
  readonly paragraphs?: { readonly start: number; readonly end: number };
  /** `p:spTgt/p:bg` — the shape's fill and outline without its text. */
  readonly backgroundOnly?: boolean;
}

export interface BehaviorTiming {
  readonly delayMs: number;
  readonly durationMs: number;
  /** 1 = plays once. `Number.POSITIVE_INFINITY` for `repeatCount="indefinite"`. */
  readonly repeatCount: number;
  readonly autoReverse: boolean;
  /** `p:cTn@accel` / `@decel` as fractions of the duration, 0..1. */
  readonly accelerate: number;
  readonly decelerate: number;
  readonly fill: TimingFill;
  readonly target: AnimationTarget;
}

/** One `p:tav` — a value at a point in the behaviour's own 0..1 timeline. */
export interface AnimationKeyframe {
  readonly time: number;
  /**
   * The authored value. `#ppt_x` style formulae are kept verbatim; the compiler
   * resolves the ones it understands and reports the rest as unsupported.
   */
  readonly value: string;
}

export interface Vector2 {
  readonly x: number;
  readonly y: number;
}

export type AnimationBehavior =
  /** `p:set` — a discrete value applied for the behaviour's duration. */
  | { readonly kind: 'set'; readonly timing: BehaviorTiming; readonly attribute: string; readonly to: string }
  /** `p:anim` — an interpolated attribute (`ppt_x`, `style.opacity`, ...). */
  | {
      readonly kind: 'animate';
      readonly timing: BehaviorTiming;
      readonly attribute: string;
      readonly from?: string;
      readonly to?: string;
      readonly by?: string;
      readonly keyframes: readonly AnimationKeyframe[];
      readonly calcMode: 'discrete' | 'linear' | 'formula';
    }
  /** `p:animEffect` — a named transition filter such as `wipe(up)`. */
  | {
      readonly kind: 'filter';
      readonly timing: BehaviorTiming;
      readonly filter: string;
      readonly transition: 'in' | 'out' | 'none';
    }
  /** `p:animScale` — values are thousandths of a percent (100000 = 100%). */
  | {
      readonly kind: 'scale';
      readonly timing: BehaviorTiming;
      readonly from?: Vector2;
      readonly to?: Vector2;
      readonly by?: Vector2;
      readonly zoomContents: boolean;
    }
  /** `p:animRot` — values are 60000ths of a degree. */
  | {
      readonly kind: 'rotate';
      readonly timing: BehaviorTiming;
      readonly from?: SixtyThousandthsDegree;
      readonly to?: SixtyThousandthsDegree;
      readonly by?: SixtyThousandthsDegree;
    }
  /** `p:animClr` — `to` is an uppercase 6-digit sRGB hex when it could be resolved. */
  | {
      readonly kind: 'color';
      readonly timing: BehaviorTiming;
      readonly colorSpace: 'rgb' | 'hsl';
      readonly attribute: string;
      readonly to?: string;
      readonly byHsl?: { readonly h: number; readonly s: Milliperecent; readonly l: Milliperecent };
    }
  /** `p:animMotion` — `path` is in the SVG-like OOXML motion grammar, in slide fractions. */
  | {
      readonly kind: 'motion';
      readonly timing: BehaviorTiming;
      readonly path?: string;
      readonly by?: Vector2;
      readonly from?: Vector2;
      readonly to?: Vector2;
      readonly origin: 'parent' | 'layout';
      readonly rotateWithPath: boolean;
    }
  /** `p:cmd` — media verbs such as `playFrom(0.0)`. */
  | {
      readonly kind: 'command';
      readonly timing: BehaviorTiming;
      readonly commandType: 'call' | 'evt' | 'verb';
      readonly command: string;
    };

export interface AnimationEffect {
  /** `p:cTn@id`, or a synthesised id when the file omits one. */
  readonly id: string;
  readonly presetClass: EffectClass;
  readonly presetId?: number;
  readonly presetSubtype?: number;
  readonly trigger: EffectTrigger;
  /** Authored start delay, relative to whatever the trigger resolves to. */
  readonly delayMs: number;
  /** Resolved start, relative to the start of the effect's group. */
  readonly startMs: number;
  readonly durationMs: number;
  readonly target: AnimationTarget;
  readonly behaviors: readonly AnimationBehavior[];
}

/**
 * One click step. `auto` groups run without waiting; everything else is a stop
 * point the player advances past with Next.
 */
export interface AnimationGroup {
  readonly index: number;
  readonly auto: boolean;
  /** Delay before the group starts once its trigger has fired. */
  readonly delayMs: number;
  readonly durationMs: number;
  readonly effects: readonly AnimationEffect[];
}

/** A `p:seq` with `nodeType="interactiveSeq"` — effects fired by clicking a shape. */
export interface InteractiveSequence {
  readonly id: string;
  readonly triggerShapeId: string;
  readonly event: string;
  readonly durationMs: number;
  readonly effects: readonly AnimationEffect[];
}

export interface SlideTiming {
  readonly groups: readonly AnimationGroup[];
  readonly interactive: readonly InteractiveSequence[];
  /** Sum of every group's duration, ignoring the waits between click steps. */
  readonly totalMs: number;
  /**
   * Timing constructs found in the file that this build does not represent,
   * as stable `animation:<what>` strings for the fidelity report.
   */
  readonly unsupported: readonly string[];
}

/** True when the slide has anything the timeline engine would actually play. */
export function hasAnimation(timing: SlideTiming | undefined): boolean {
  return timing !== undefined && (timing.groups.length > 0 || timing.interactive.length > 0);
}
